import React, { useEffect } from 'react';

interface PromptStatsInfoModalProps {
  open: boolean;
  onClose: () => void;
}

const PromptStatsInfoModal: React.FC<PromptStatsInfoModalProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="ps-info-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ps-info-modal-title"
      onClick={onClose}
    >
      <div
        className="ps-info-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
      >
        <header className="ps-info-modal-header">
          <div>
            <p className="ps-info-modal-eyebrow">Prompt analytics</p>
            <h2 className="ps-info-modal-title" id="ps-info-modal-title">
              What do these numbers mean?
            </h2>
          </div>
          <button
            type="button"
            className="ps-info-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="ps-info-modal-body">
          <p className="ps-info-modal-lede">
            Each prompt response on your profile collects recruiter engagement signals. Here&apos;s
            how the four pills under every prompt are calculated.
          </p>

          <ul className="ps-info-metric-list">
            <li className="ps-info-metric ps-info-metric--views">
              <div className="ps-info-metric-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <div className="ps-info-metric-body">
                <h3>Views</h3>
                <p>
                  Total number of times recruiters opened or replayed this prompt. Every expand,
                  every video re-watch counts as one view.
                </p>
              </div>
            </li>

            <li className="ps-info-metric ps-info-metric--likes">
              <div className="ps-info-metric-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </svg>
              </div>
              <div className="ps-info-metric-body">
                <h3>Likes</h3>
                <p>
                  How many recruiters gave this prompt a like. Likes are the strongest positive
                  signal — they usually precede a chat request.
                </p>
              </div>
            </li>

            <li className="ps-info-metric ps-info-metric--impressions">
              <div className="ps-info-metric-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="M7 16l4-6 4 3 5-8" />
                </svg>
              </div>
              <div className="ps-info-metric-body">
                <h3>Impressions</h3>
                <p>
                  Number of unique recruiters who&apos;ve engaged with this prompt. A single
                  recruiter who opens the prompt three times still counts as one impression.
                </p>
              </div>
            </li>

            <li className="ps-info-metric ps-info-metric--score">
              <div className="ps-info-metric-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20V10" />
                  <path d="M18 20V4" />
                  <path d="M6 20v-4" />
                </svg>
              </div>
              <div className="ps-info-metric-body">
                <h3>Score</h3>
                <p>
                  A simple engagement score that combines views and likes so you can compare prompts
                  at a glance.
                </p>
                <div className="ps-info-formula">
                  <div className="ps-info-formula-eq">Score = (Likes × 5) + (Views × 1)</div>
                  <ul className="ps-info-formula-list">
                    <li>
                      Each <strong>like</strong> adds <strong>+5</strong> — strongest signal.
                    </li>
                    <li>
                      Each <strong>view</strong> adds <strong>+1</strong> — shows recruiters cared
                      enough to open it.
                    </li>
                  </ul>
                  <p className="ps-info-formula-example">
                    Example: 6 views + 1 like = <strong>11.0</strong>. A prompt with 2 likes and 4
                    views scores <strong>14.0</strong>.
                  </p>
                </div>
              </div>
            </li>
          </ul>

          <div className="ps-info-modal-foot">
            <p>
              <strong>Tip:</strong> short, specific answers with a video tend to convert views into
              likes much faster.
            </p>
          </div>
        </div>

        <footer className="ps-info-modal-footer">
          <button type="button" className="ps-info-modal-cta" onClick={onClose}>
            Got it
          </button>
        </footer>
      </div>
    </div>
  );
};

export default PromptStatsInfoModal;
